import { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtPeriod } from "@/lib/api";
import { PageHeader, Section, Btn, Modal, Input } from "@/components/Shared";
import { useAuth } from "@/lib/auth";
import { CalendarDays, Plus, Pencil, Trash2, Upload, Search, AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, List, Grid3X3 } from "lucide-react";
import { toast } from "sonner";
import { openConfirm } from "@/lib/dialogs";

// Settlement Holidays — single component shared across Admin / Partner /
// Merchant portals. Admin sees the full CRUD + bulk-upload toolbar; the
// partner & merchant variants are read-only with the same KPI strip and
// table. The nationwide calendar applies to every payment gateway, so we
// don't differentiate by PG anywhere on this page.

const EMPTY = { date: "", name: "", notes: "" };

export default function Holidays() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "demo";

  const [rows, setRows] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [q, setQ] = useState("");
  const [modal, setModal] = useState({ open: false, form: EMPTY, editId: null });
  const [importing, setImporting] = useState(false);
  // View mode — "calendar" (month grid, default per user request) or "list"
  // (the original line-item table). Persisted to localStorage so each user
  // remembers their preference.
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("holidays:view") || "calendar"; } catch (_) { return "calendar"; }
  });
  useEffect(() => { try { localStorage.setItem("holidays:view", view); } catch (_) { /* ignore */ } }, [view]);
  // Month being shown in calendar view. Defaults to "today" — picks up the
  // first month with any holidays once data loads.
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() };  // m is 0-indexed
  });
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [list, cov] = await Promise.all([
        api.get("/holidays"),
        api.get("/holidays/coverage"),
      ]);
      setRows(list.data);
      setCoverage(cov.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load holidays");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // Splits the calendar into past / today / upcoming for KPIs and the
  // "Next holiday in N days" hero card.
  const stats = useMemo(() => {
    if (!rows) return null;
    const upcoming = rows.filter((r) => r.date >= today);
    const past = rows.length - upcoming.length;
    const next = upcoming[0] || null;
    const daysUntil = next ? Math.max(0, Math.ceil((new Date(next.date) - new Date(today)) / 86400000)) : null;
    return { total: rows.length, upcoming: upcoming.length, past, next, daysUntil };
  }, [rows, today]);

  const visible = useMemo(() => {
    if (!rows) return [];
    if (!q.trim()) return rows;
    const ql = q.trim().toLowerCase();
    return rows.filter((r) =>
      r.date.toLowerCase().includes(ql)
      || r.name.toLowerCase().includes(ql)
      || (r.notes || "").toLowerCase().includes(ql)
    );
  }, [rows, q]);

  // ---- Admin actions ------------------------------------------------------
  const openCreate = () => setModal({ open: true, form: EMPTY, editId: null });
  const openEdit = (r) => setModal({ open: true, form: { date: r.date, name: r.name, notes: r.notes || "" }, editId: r.id });

  const save = async () => {
    const f = modal.form;
    if (!f.date || !f.name.trim()) { toast.error("Date and Name are required"); return; }
    try {
      if (modal.editId) {
        await api.put(`/holidays/${modal.editId}`, f);
        toast.success("Holiday updated");
      } else {
        await api.post("/holidays", f);
        toast.success("Holiday added");
      }
      setModal({ open: false, form: EMPTY, editId: null });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (r) => {
    const ok = await openConfirm({
      title: "Remove this holiday?",
      message: `${r.name} on ${r.date} will be removed from the calendar. Partners and merchants will no longer see this date.`,
      confirmLabel: "Remove",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/holidays/${r.id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/holidays/bulk-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported: ${data.inserted} new, ${data.updated} updated, ${data.skipped} skipped`);
      if (data.errors?.length) toast.warning(`${data.errors.length} issues`, { description: data.errors.slice(0, 3).join(" · ") });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    }
    setImporting(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  // ---- Render -------------------------------------------------------------
  if (rows === null) {
    return (
      <div className="p-10 text-[var(--muted)] text-sm" data-testid="holidays-loading">Loading holidays…</div>
    );
  }

  return (
    <div data-testid="holidays-page">
      <PageHeader
        title="Settlement Holidays"
        subtitle="Bank settlement holiday calendar — applies to all payment gateways"
        actions={isAdmin ? (
          <>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xlsm"
              onChange={onUpload}
              className="hidden"
              data-testid="holidays-file-input"
            />
            <Btn variant="ghost" testid="holidays-import-btn" disabled={importing} onClick={() => fileRef.current?.click()}>
              <Upload size={14} /> {importing ? "Importing…" : "Bulk import"}
            </Btn>
            <Btn variant="accent" testid="holidays-add-btn" onClick={openCreate}>
              <Plus size={14} /> Add holiday
            </Btn>
          </>
        ) : null}
      />
      <div className="p-4 sm:p-10 max-w-5xl space-y-6">

        {/* Coverage banner — admin gets a stronger nudge when calendar is
            running thin (≤30 days remaining or past Mar 15). */}
        {coverage && coverage.alert_level !== "ok" && (
          <div
            data-testid={`holidays-alert-${coverage.alert_level}`}
            className={`flex items-start gap-3 p-4 rounded-sm border ${
              coverage.alert_level === "urgent"
                ? "bg-red-50 border-red-200 text-red-900"
                : "bg-amber-50 border-amber-200 text-amber-900"
            }`}
          >
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-[13.5px]">
                {coverage.alert_level === "urgent" ? "Action required" : "Calendar running low"}
              </div>
              <div className="text-[12.5px] mt-1 leading-relaxed">{coverage.message}</div>
              {isAdmin && (
                <div className="text-[12px] mt-2">
                  Use <b>Bulk import</b> above (.csv or .xlsx with columns <code className="text-[11px] bg-white/50 px-1 py-0.5 rounded-sm">date, name, notes</code>) to refresh the calendar.
                </div>
              )}
            </div>
          </div>
        )}

        {/* KPI strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 auto-rows-fr" data-testid="holidays-kpis">
          <Kpi
            icon={CalendarDays}
            label="Next holiday"
            value={stats?.next ? stats.next.name : "—"}
            sub={stats?.next ? `${formatHumanDate(stats.next.date)} · in ${stats.daysUntil} day${stats.daysUntil === 1 ? "" : "s"}` : "No upcoming"}
            testid="holidays-kpi-next"
          />
          <Kpi
            icon={CheckCircle2}
            label="Upcoming"
            value={stats?.upcoming ?? 0}
            sub="Future-dated entries"
            testid="holidays-kpi-upcoming"
          />
          <Kpi
            icon={CalendarDays}
            label="Total in calendar"
            value={stats?.total ?? 0}
            sub={`${stats?.past ?? 0} past · ${stats?.upcoming ?? 0} upcoming`}
            testid="holidays-kpi-total"
          />
          <Kpi
            icon={CalendarDays}
            label="Calendar runs to"
            value={coverage?.end_date ? formatHumanDate(coverage.end_date) : "—"}
            sub={coverage?.days_remaining != null ? `${coverage.days_remaining} day${coverage.days_remaining === 1 ? "" : "s"} of cover` : "—"}
            testid="holidays-kpi-coverage"
          />
        </div>

        <Section
          title={view === "calendar"
            ? `${MONTH_NAMES[calMonth.m]} ${calMonth.y}`
            : `${visible.length} of ${rows.length} ${rows.length === 1 ? "holiday" : "holidays"}`}
          right={
            <div className="flex items-center gap-1.5">
              {view === "calendar" && (
                <>
                  <button
                    type="button"
                    onClick={() => setCalMonth(stepMonth(calMonth, -1))}
                    className="h-8 w-8 grid place-items-center hover:bg-[var(--surface-2)] rounded-sm border border-[var(--line)]"
                    data-testid="holidays-cal-prev"
                    aria-label="Previous month"
                  ><ChevronLeft size={14} /></button>
                  <button
                    type="button"
                    onClick={() => setCalMonth(todayMonth())}
                    className="h-8 px-2.5 hover:bg-[var(--surface-2)] rounded-sm border border-[var(--line)] text-[11.5px] font-medium"
                    data-testid="holidays-cal-today"
                  >Today</button>
                  <button
                    type="button"
                    onClick={() => setCalMonth(stepMonth(calMonth, 1))}
                    className="h-8 w-8 grid place-items-center hover:bg-[var(--surface-2)] rounded-sm border border-[var(--line)]"
                    data-testid="holidays-cal-next"
                    aria-label="Next month"
                  ><ChevronRight size={14} /></button>
                  <span className="mx-1 h-5 w-px bg-[var(--line-2)]" />
                </>
              )}
              <button
                type="button"
                onClick={() => setView("calendar")}
                className={`h-8 px-2.5 rounded-sm border text-[11.5px] font-medium inline-flex items-center gap-1.5 ${view === "calendar" ? "border-[var(--ink-2)] bg-[var(--surface-2)] text-[var(--ink)]" : "border-[var(--line)] text-[var(--muted)] hover:text-[var(--ink)]"}`}
                data-testid="holidays-view-calendar"
              ><Grid3X3 size={12} /> Calendar</button>
              <button
                type="button"
                onClick={() => setView("list")}
                className={`h-8 px-2.5 rounded-sm border text-[11.5px] font-medium inline-flex items-center gap-1.5 ${view === "list" ? "border-[var(--ink-2)] bg-[var(--surface-2)] text-[var(--ink)]" : "border-[var(--line)] text-[var(--muted)] hover:text-[var(--ink)]"}`}
                data-testid="holidays-view-list"
              ><List size={12} /> List</button>
            </div>
          }
        >
          {view === "list" && (
            <div className="px-4 pt-3 pb-2">
              <div className="relative w-full sm:w-72">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)] pointer-events-none" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search date / name / notes…"
                  className="pl-8"
                  data-testid="holidays-search"
                />
              </div>
            </div>
          )}
          {view === "calendar" ? (
            <CalendarGrid
              year={calMonth.y}
              month={calMonth.m}
              rows={rows}
              today={today}
              isAdmin={isAdmin}
              onEdit={openEdit}
              onAddOnDate={(iso) => setModal({ open: true, form: { ...EMPTY, date: iso }, editId: null })}
            />
          ) : (
          <div className="overflow-auto">
            <table className="table-data w-full">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Day</th>
                  <th>Holiday</th>
                  <th>Notes</th>
                  <th>Status</th>
                  {isAdmin && <th></th>}
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && (
                  <tr><td colSpan={isAdmin ? 6 : 5} className="px-4 py-8 text-center text-[var(--muted)]">
                    {rows.length === 0 ? "No holidays in calendar yet." : "No matches for your search."}
                  </td></tr>
                )}
                {visible.map((r) => {
                  const isPast = r.date < today;
                  const isToday = r.date === today;
                  return (
                    <tr key={r.id} data-testid={`holiday-row-${r.id}`}>
                      <td className="num font-medium">{formatHumanDate(r.date)}</td>
                      <td>{r.day_of_week}</td>
                      <td>{r.name}</td>
                      <td className="text-[12px] text-[var(--muted)] max-w-[260px] truncate">{r.notes || "—"}</td>
                      <td>
                        {isToday ? (
                          <span className="inline-flex items-center text-[10.5px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[var(--accent)]/10 text-[var(--accent)] border border-[var(--accent)]/30">Today</span>
                        ) : isPast ? (
                          <span className="inline-flex items-center text-[10.5px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-500 border border-slate-200">Past</span>
                        ) : (
                          <span className="inline-flex items-center text-[10.5px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200">Upcoming</span>
                        )}
                      </td>
                      {isAdmin && (
                        <td className="text-right">
                          <button onClick={() => openEdit(r)} className="p-1.5 hover:bg-[var(--surface-2)] rounded-sm" data-testid={`holiday-edit-${r.id}`}>
                            <Pencil size={13} />
                          </button>
                          <button onClick={() => remove(r)} className="p-1.5 hover:bg-red-50 text-[var(--negative)] rounded-sm" data-testid={`holiday-del-${r.id}`}>
                            <Trash2 size={13} />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </Section>

        {!isAdmin && (
          <div className="text-[11.5px] text-[var(--muted)] leading-relaxed border-l-2 border-[var(--line-2)] pl-3">
            On these dates, settlements via every payment gateway are paused for the day and resume on the next working day. Plan your cash flow accordingly.
          </div>
        )}
      </div>

      {/* Add / Edit modal — admin only */}
      {isAdmin && (
        <Modal
          open={modal.open}
          onClose={() => setModal({ open: false, form: EMPTY, editId: null })}
          title={modal.editId ? "Edit holiday" : "Add holiday"}
          testid="holiday-modal"
        >
          <div className="space-y-3">
            <div>
              <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Date *</label>
              <Input
                type="date"
                value={modal.form.date}
                onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, date: e.target.value } }))}
                data-testid="holiday-form-date"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Holiday name *</label>
              <Input
                placeholder="e.g. Republic Day"
                value={modal.form.name}
                onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, name: e.target.value } }))}
                data-testid="holiday-form-name"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Notes (optional)</label>
              <Input
                placeholder="e.g. RBI announcement"
                value={modal.form.notes}
                onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, notes: e.target.value } }))}
                data-testid="holiday-form-notes"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <Btn variant="ghost" onClick={() => setModal({ open: false, form: EMPTY, editId: null })}>Cancel</Btn>
            <Btn variant="accent" testid="holiday-save" onClick={save}>{modal.editId ? "Save" : "Add"}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---- helpers --------------------------------------------------------------

function formatHumanDate(iso) {
  // 2026-05-30 → "30 May 2026"
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="rise bg-[var(--surface)] border border-[var(--line)] p-5 flex flex-col h-full">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold flex items-center gap-1.5">
        <Icon size={12} strokeWidth={2.2} className="text-[var(--accent)]" />
        {label}
      </div>
      <div className="mt-3 text-[18px] font-semibold text-[var(--ink)] truncate" title={value}>{value}</div>
      <div className="text-[11px] text-[var(--muted)] mt-auto pt-1.5">{sub}</div>
    </div>
  );
}


// ---- Calendar grid view --------------------------------------------------

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function todayMonth() {
  const d = new Date();
  return { y: d.getFullYear(), m: d.getMonth() };
}
function stepMonth({ y, m }, delta) {
  const dt = new Date(y, m + delta, 1);
  return { y: dt.getFullYear(), m: dt.getMonth() };
}
function pad2(n) { return String(n).padStart(2, "0"); }
function isoFromYMD(y, m, d) { return `${y}-${pad2(m + 1)}-${pad2(d)}`; }

/**
 * Month grid view — Sun→Sat columns, holiday names rendered as colored
 * pills on the matching date cell. Clicking a cell on admin opens "Add
 * holiday" pre-filled with that date; clicking a holiday pill opens
 * Edit. On partner/merchant the grid is fully read-only.
 */
function CalendarGrid({ year, month, rows, today, isAdmin, onEdit, onAddOnDate }) {
  // Build a date-keyed map of holidays for O(1) cell lookup. Multiple
  // holidays on the same day (rare but possible — e.g. Buddha + Maharashtra)
  // are stacked.
  const byDate = new Map();
  for (const r of rows) {
    if (!byDate.has(r.date)) byDate.set(r.date, []);
    byDate.get(r.date).push(r);
  }

  // Days grid: includes a leading offset (empty cells before day 1) so the
  // first row starts on Sunday. Padding cells use null.
  const firstWeekday = new Date(year, month, 1).getDay();  // 0=Sun
  const lastDay = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= lastDay; d++) cells.push(d);
  // Pad trailing cells so the grid is always a multiple of 7 (clean borders).
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="p-3 sm:p-5" data-testid="holidays-calendar-grid">
      {/* Day-of-week header */}
      <div className="grid grid-cols-7 mb-1.5">
        {DAY_LABELS.map((d, i) => (
          <div
            key={d}
            className={`text-[10.5px] uppercase tracking-[0.2em] font-semibold py-1.5 text-center ${i === 0 ? "text-[var(--negative)]" : "text-[var(--muted)]"}`}
          >{d}</div>
        ))}
      </div>
      {/* Date cells */}
      <div className="grid grid-cols-7 border-t border-l border-[var(--line)]">
        {cells.map((d, idx) => {
          if (d === null) {
            return (
              <div
                key={`pad-${idx}`}
                className="border-r border-b border-[var(--line)] bg-[var(--surface-2)]/30 min-h-[88px]"
              />
            );
          }
          const iso = isoFromYMD(year, month, d);
          const dayOfWeek = new Date(year, month, d).getDay();  // 0=Sun
          const isToday = iso === today;
          const isSunday = dayOfWeek === 0;
          const items = byDate.get(iso) || [];
          return (
            <div
              key={iso}
              data-testid={`holidays-cal-cell-${iso}`}
              onClick={(e) => {
                if (!isAdmin) return;
                // Don't trigger when clicking a holiday pill (its own onClick fires).
                if (e.target.closest("[data-pill]")) return;
                onAddOnDate?.(iso);
              }}
              className={`border-r border-b border-[var(--line)] min-h-[88px] p-1.5 flex flex-col gap-1 transition-colors ${isAdmin ? "cursor-pointer hover:bg-[var(--surface-2)]/60" : ""} ${isToday ? "bg-[var(--accent)]/[0.04] ring-1 ring-inset ring-[var(--accent)]/40" : isSunday && items.length === 0 ? "bg-red-50/30" : "bg-[var(--surface)]"}`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-[12px] tabular-nums font-semibold ${isToday ? "text-[var(--accent)]" : isSunday ? "text-[var(--negative)]/80" : "text-[var(--ink)]"}`}>
                  {pad2(d)}
                </span>
                {isToday && <span className="text-[9px] uppercase tracking-wider px-1 py-0 rounded-sm bg-[var(--accent)] text-white font-semibold">Today</span>}
              </div>
              <div className="flex flex-col gap-0.5 overflow-hidden">
                {items.map((h) => (
                  <button
                    key={h.id}
                    type="button"
                    data-pill
                    onClick={(e) => { e.stopPropagation(); if (isAdmin) onEdit?.(h); }}
                    className={`text-left text-[10.5px] leading-tight px-1.5 py-0.5 truncate rounded-sm font-medium ${pillToneFor(h)} ${isAdmin ? "cursor-pointer hover:brightness-95" : "cursor-default"}`}
                    title={`${h.name}${h.notes ? ` · ${h.notes}` : ""}`}
                    data-testid={`holidays-cal-pill-${h.id}`}
                  >
                    {h.name}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {/* Legend */}
      <div className="flex items-center gap-3 flex-wrap mt-3 text-[10.5px] text-[var(--muted)]">
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-indigo-100 border border-indigo-200" /> Sunday / Saturday bank</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-amber-100 border border-amber-200" /> Festival</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-rose-100 border border-rose-200" /> Manually added (RBI ad-hoc)</span>
        {isAdmin && <span className="ml-auto text-[10.5px] text-[var(--muted)]">Tip: click an empty cell to add a holiday on that date.</span>}
      </div>
    </div>
  );
}

// Visual tone for each holiday pill — separates the routine Sunday/Saturday
// rows from named festivals so a busy month is still scannable.
function pillToneFor(h) {
  const name = (h.name || "").toLowerCase();
  if (h.source === "manual") return "bg-rose-100 text-rose-900 border border-rose-200";
  if (name.includes("sunday") || name.includes("saturday bank")) return "bg-indigo-100 text-indigo-900 border border-indigo-200";
  return "bg-amber-100 text-amber-900 border border-amber-200";
}
